import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Elements, PaymentElement, useElements } from "@stripe/react-stripe-js";
import { fetchSetupIntentSecret, updateSetupIntent } from "./hooks";

const inputBorder = "#8B8C8C";
const activeInputBorder = "#054046";
const labelColor = "#181919";
const placeholderColor = "#747575";

// this is the url for the Nunito font as litcharts uses it, though I couldn't get it to work with stripe.
// const fonts = [{ name: 'Nunito Sans', src: 'url(https://d3k3g22gcb0gya.cloudfront.net/assets/NunitoSans-Regular-13ad641781e6dd5f2134fcfc9b1589e3c66902b69ccbb68b74d3bd139c1e324e.ttf)' }];

const fonts = [
  { cssSrc: "https://fonts.googleapis.com/css?family=Nunito+Sans" }
];

const appearance = {
  rules: {
    ".Input": {
      borderColor: labelColor,
      fontSize: "16px",
      padding: "10px"
    },
    ".Input:focus": {
      borderColor: activeInputBorder
    },
    ".Input--empty": {
      borderColor: inputBorder
    },
    ".Label": {
      color: labelColor,
      fontSize: "16px",
      lineHeight: "26px"
    },
    ".Tab": {
      borderColor: inputBorder,
      color: placeholderColor
    },
    ".Tab:active": {
      borderColor: inputBorder,
      color: labelColor
    },
    ".Tab:focus": {
      borderColor: inputBorder,
      color: activeInputBorder
    },
    ".Tab:hover": {
      borderColor: inputBorder,
      color: labelColor
    },
    ".Tab--selected": {
      borderColor: activeInputBorder,
      color: activeInputBorder
    },
    ".TabIcon": {
      color: placeholderColor
    },
    ".TabIcon:active": {
      color: labelColor
    },
    ".TabIcon:focus": {
      color: activeInputBorder
    },
    ".TabIcon:hover": {
      color: labelColor
    },
    ".TermsText": {
      color: placeholderColor,
      lineHeight: "20px",
      padding: "0px"
    }
  },
  variables: {
    borderRadius: "4px",
    colorBackground: "white",
    colorDanger: "#BB5050",
    colorIconTabSelected: activeInputBorder,
    colorPrimary: labelColor,
    colorText: labelColor,
    colorTextPlaceholder: placeholderColor,
    fontFamily: "Nunito Sans",
    fontLineHeight: "25px",
    iconChevronDownColor: labelColor
  }
};

// Note: We support both PaymentIntents and SetupIntents from Stripe.  In both instances, we need
// a client secret and we therefore pass in the prop isPaymentIntent to decide whether we send Stripe
// down the SetupIntent vs PaymentIntent route.
export default function StripeElements({
  setElements,
  setupIntentsPath,
  onReady,
  subscriptionType,
  isPaymentIntent,
  clientSecret: propClientSecret
}) {
  const ref = useRef();

  const [clientSecret, setupIntentId] = isPaymentIntent
    ? [propClientSecret, null]
    : fetchSetupIntentSecret(setupIntentsPath, subscriptionType);

  // This useEffect is to handle updating the subscription plan via the Sell Form with
  // the subscription radio buttons for SetupIntents. We need to update the Setup Intent with the expected
  // mandate options. We could just create a new setup intent and reload the form, but that
  // would blow away anything the user typed in.
  useEffect(() => {
    if (!isPaymentIntent && setupIntentId && ref.current) {
      updateSetupIntent(setupIntentId, subscriptionType);
    }
    ref.current = true;
  }, [setupIntentId, subscriptionType, isPaymentIntent]);

  if (!clientSecret) return null;

  const stripeOptions = { appearance, clientSecret, fonts };

  return (
    <Elements stripe={window.LitCharts.stripe} options={stripeOptions}>
      <StripePaymentElement setElements={setElements} onReady={onReady} />
    </Elements>
  );
}

StripeElements.propTypes = {
  clientSecret: PropTypes.string.isRequired,
  isPaymentIntent: PropTypes.bool,
  onReady: PropTypes.func.isRequired,
  setElements: PropTypes.func.isRequired
};

StripeElements.defaultProps = {
  isPaymentIntent: false
};

function StripePaymentElement(props) {
  const elements = useElements();
  useEffect(() => {
    props.setElements(elements);
  }, []);

  return <PaymentElement onReady={props.onReady} />;
}

StripePaymentElement.propTypes = {
  onReady: PropTypes.func.isRequired,
  setElements: PropTypes.func.isRequired
};
